// XR8 pipeline module that captures the AR canvas after GPU render.
// Call requestCapture() to get a JPEG data URL of the next rendered frame.

import { logger } from '../utils/logger';

let arCanvas: HTMLCanvasElement | null = null;
let pendingResolve: ((dataUrl: string) => void) | null = null;

export function capturePipelineModule(): XR8PipelineModule {
  return {
    name: 'capture-module',
    onStart: ({ canvas }) => {
      arCanvas = canvas;
    },
    onProcessGpu: () => {
      if (!pendingResolve || !arCanvas) return;
      const resolve = pendingResolve;
      pendingResolve = null;
      try {
        resolve(arCanvas.toDataURL('image/jpeg', 0.92));
      } catch (err) {
        logger.error('Canvas capture failed:', err);
        resolve('');
      }
    },
    onDestroy: () => {
      arCanvas = null;
      pendingResolve = null;
    },
  };
}

/**
 * Returns a promise that resolves with a JPEG data URL of the next frame.
 * Falls back to a direct toDataURL() call after 300 ms if onProcessGpu
 * never fires (e.g. the hook isn't supported in this XR8 build).
 */
export function requestCapture(): Promise<string> {
  return new Promise((resolve) => {
    if (!arCanvas) {
      logger.warn('requestCapture: no AR canvas available');
      resolve('');
      return;
    }
    pendingResolve = resolve;

    // Safety timeout — attempt direct capture if the hook never fires.
    const canvas = arCanvas;
    setTimeout(() => {
      if (pendingResolve !== resolve) return;
      pendingResolve = null;
      logger.warn('requestCapture: onProcessGpu timeout, falling back to direct toDataURL');
      try {
        resolve(canvas.toDataURL('image/jpeg', 0.92));
      } catch {
        resolve('');
      }
    }, 300);
  });
}
